/** `query_metadata` is diagnostic-only and must not drive UI. */
export type AiWidgetQueryMetadata = Record<string, unknown>;

export interface AiWidgetResponseEnvelope<TData> {
  data: TData;
  query_metadata?: AiWidgetQueryMetadata;
}

export const conversationBalanceRoles = ['agent', 'customer', 'chatbot'] as const;
export type ConversationBalanceRole = (typeof conversationBalanceRoles)[number];

export const conversationBalanceMetricUnits = ['messages', 'seconds', 'characters'] as const;
export type ConversationBalanceMetricUnit = (typeof conversationBalanceMetricUnits)[number];

export const conversationBalanceMetricNames = [
  'message_count',
  'response_time',
  'handle_time',
  'message_length',
] as const;
export type ConversationBalanceMetricName = (typeof conversationBalanceMetricNames)[number];

export interface ConversationBalanceSeriesPoint {
  role: ConversationBalanceRole;
  value: number;
  rawValue?: number;
}

export interface ConversationBalanceMetricDto {
  name: ConversationBalanceMetricName;
  label: string;
  unit?: ConversationBalanceMetricUnit;
  series: ConversationBalanceSeriesPoint[];
}

export interface ConversationBalanceDataDto {
  isPercentage: boolean;
  metrics: ConversationBalanceMetricDto[];
}

export type ConversationBalanceApiResponse = AiWidgetResponseEnvelope<ConversationBalanceDataDto>;

export interface NamedSeriesPoint {
  name: string;
  value: number;
}

export interface ChurnRiskDistributionDataDto {
  series: NamedSeriesPoint[];
  isPercentage: boolean;
  showLabels: boolean;
}

export type ChurnRiskDistributionApiResponse =
  AiWidgetResponseEnvelope<ChurnRiskDistributionDataDto>;

export interface CustomerEffortDistributionDto {
  series: NamedSeriesPoint[];
  high_effort_pct: number;
  isPercentage: boolean;
}

export type CustomerEffortDistributionApiResponse =
  AiWidgetResponseEnvelope<CustomerEffortDistributionDto>;

export interface CustomerEffortTouchpointDto extends CustomerEffortDistributionDto {
  touchpoint: string;
}

export interface CustomerEffortBreakdownDataDto {
  overall: CustomerEffortDistributionDto;
  by_touchpoint: CustomerEffortTouchpointDto[];
}

export type CustomerEffortBreakdownApiResponse =
  AiWidgetResponseEnvelope<CustomerEffortBreakdownDataDto>;

export interface FrictionSignalItemDto {
  label: string;
  count: number;
  pct: number;
}

export interface FrictionSignalsFrequencyDataDto {
  items: FrictionSignalItemDto[];
  total_sessions: number;
}

export type FrictionSignalsFrequencyApiResponse =
  AiWidgetResponseEnvelope<FrictionSignalsFrequencyDataDto>;

export const dispositionChangeBuckets = ['improved', 'unchanged', 'degraded'] as const;
export type DispositionChangeBucket = (typeof dispositionChangeBuckets)[number];

export interface DispositionChangeSeriesPoint {
  name: DispositionChangeBucket;
  value: number;
}

export interface DispositionChangeReasonItemDto {
  label: string;
  count: number;
}

export interface DispositionChangeReasonGroupDto {
  bucket: DispositionChangeBucket;
  items: DispositionChangeReasonItemDto[];
}

export interface DispositionChangeDistributionDataDto {
  isPercentage: boolean;
  series: DispositionChangeSeriesPoint[];
  dominant_bucket: DispositionChangeBucket;
  dominant_pct: number;
  reasons: DispositionChangeReasonGroupDto[];
}

export type DispositionChangeDistributionApiResponse =
  AiWidgetResponseEnvelope<DispositionChangeDistributionDataDto>;
